import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { Card, Col, Dropdown, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SimpleBarReact from 'simplebar-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import { getItemFromStore, setItemToStore } from 'helpers/utils';
import { launcherModules } from './moduleLauncherConfig';
import { getWorkspaceMode, isRouteVisibleForMode } from 'helpers/workspace';

const MODULE_USAGE_STORE_KEY = 'moduleLauncherUsage';

const getUsageMap = () =>
  getItemFromStore(MODULE_USAGE_STORE_KEY, {}) || {};

const setUsageMap = usageMap => {
  setItemToStore(MODULE_USAGE_STORE_KEY, usageMap);
};

const matchModuleByPathname = pathname => {
  return launcherModules.find(module => {
    if (module.link === '/') {
      return pathname === '/';
    }
    return pathname === module.link || pathname.startsWith(`${module.link}/`);
  });
};

const trackModuleUsage = moduleId => {
  if (!moduleId) return;
  const currentUsage = getUsageMap();
  const nextUsage = {
    ...currentUsage,
    [moduleId]: Number(currentUsage[moduleId] || 0) + 1
  };
  setUsageMap(nextUsage);
  return nextUsage;
};

const NineDotMenu = () => {
  const [show, setShow] = useState(null);
  const [usageMap, setUsageState] = useState(getUsageMap());
  const { t } = useTranslation();
  const { hasPermission, hasAnyPermission, userData, isSuperAdmin } = useAuth();
  const location = useLocation();
  const lastTrackedModuleRef = useRef('');

  useEffect(() => {
    window.addEventListener('scroll', () => {
      window.innerWidth < 1200 && setShow(false);
    });
  }, []);

  useEffect(() => {
    const matchedModule = matchModuleByPathname(location.pathname);
    if (!matchedModule || lastTrackedModuleRef.current === matchedModule.id) {
      return;
    }

    lastTrackedModuleRef.current = matchedModule.id;
    const nextUsage = trackModuleUsage(matchedModule.id);
    if (nextUsage) {
      setUsageState(nextUsage);
    }
  }, [location.pathname]);

  const visibleModules = useMemo(() => {
    const currentMode = getWorkspaceMode(userData);
    const filteredModules = launcherModules.filter(module => {
      if (!isRouteVisibleForMode(currentMode, module.modes)) {
        return false;
      }
      if (module.requiresSuperAdmin && !isSuperAdmin()) {
        return false;
      }
      if (module.requiredPermission && !hasPermission(module.requiredPermission)) {
        return false;
      }

      if (
        module.requiredAnyPermissions &&
        !hasAnyPermission(module.requiredAnyPermissions)
      ) {
        return false;
      }

      return true;
    });

    return filteredModules.sort((leftModule, rightModule) => {
      const rightUsage = Number(usageMap[rightModule.id] || 0);
      const leftUsage = Number(usageMap[leftModule.id] || 0);
      if (rightUsage !== leftUsage) {
        return rightUsage - leftUsage;
      }
      return launcherModules.findIndex(module => module.id === leftModule.id) -
        launcherModules.findIndex(module => module.id === rightModule.id);
    });
  }, [hasAnyPermission, hasPermission, isSuperAdmin, usageMap, userData]);

  const handleModuleClick = moduleId => {
    const nextUsage = trackModuleUsage(moduleId);
    if (nextUsage) {
      setUsageState(nextUsage);
    }
    setShow(false);
  };

  if (!visibleModules.length) {
    return null;
  }

  return (
    <Dropdown navbar={true} as="li" show={show} onToggle={() => setShow(!show)}>
      <Dropdown.Toggle
        bsPrefix="toggle"
        as={Link}
        to="#!"
        className="nav-link px-2 nine-dots"
        style={{
          borderRadius: '999px',
          background: 'linear-gradient(135deg, rgba(43,108,176,0.12), rgba(66,153,225,0.06))',
          border: '1px solid rgba(43,108,176,0.18)'
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <circle cx="2" cy="2" r="2" fill="#2B6CB0"></circle>
            <circle cx="2" cy="8" r="2" fill="#2B6CB0"></circle>
            <circle cx="2" cy="14" r="2" fill="#2B6CB0"></circle>
            <circle cx="8" cy="8" r="2" fill="#2B6CB0"></circle>
            <circle cx="8" cy="14" r="2" fill="#2B6CB0"></circle>
            <circle cx="14" cy="8" r="2" fill="#2B6CB0"></circle>
            <circle cx="14" cy="14" r="2" fill="#2B6CB0"></circle>
            <circle cx="8" cy="2" r="2" fill="#2B6CB0"></circle>
            <circle cx="14" cy="2" r="2" fill="#2B6CB0"></circle>
          </svg>
          <span className="text-700 fw-semi-bold fs--2 d-none d-xl-inline">
            {t('SEARCHBOX.SECTIONS.MODULES')}
          </span>
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="dropdown-menu-card dropdown-menu-end dropdown-caret dropdown-caret-bg"
        show={show}
        style={{ width: 'min(90vw, 680px)', maxWidth: 'calc(100vw - 1rem)' }}
      >
        <Card className="dropdown-menu-end shadow border-0 overflow-hidden" style={{ width: '100%' }}>
          <Card.Header className="bg-light py-2 px-3 border-bottom">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">{t('SEARCHBOX.SECTIONS.MODULES')}</h6>
              <span className="badge badge-soft-primary rounded-pill">
                {visibleModules.length}
              </span>
            </div>
          </Card.Header>
          <SimpleBarReact style={{ width: '100%', maxHeight: '72vh' }}>
            <Card.Body className="p-3">
              <Row className="text-center g-2">
                {visibleModules.map(module => (
                  <QuickLinkItem
                    key={module.id}
                    title={module.titleKey.includes('.') ? t(module.titleKey) : module.titleKey}
                    link={module.link}
                    icon={module.icon}
                    usageCount={Number(usageMap[module.id] || 0)}
                    onClick={() => handleModuleClick(module.id)}
                  />
                ))}
              </Row>
            </Card.Body>
          </SimpleBarReact>
        </Card>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const QuickLinkItem = ({ title, link, icon, usageCount, onClick }) => {
  return (
    <Col xs={6} md={4} xl={3}>
      <Link
        className="d-flex flex-column align-items-center px-2 py-2 rounded-3 text-decoration-none h-100 border shadow-sm"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.96), rgba(244,247,255,0.95))',
          transition: 'all 180ms ease'
        }}
        to={link || '#!'}
        onClick={onClick}
      >
        <div className="d-flex align-items-center justify-content-center mb-2">
          <div
            className="rounded-circle bg-soft-primary text-primary d-flex align-items-center justify-content-center"
            style={{ width: '42px', height: '42px' }}
          >
            <FontAwesomeIcon icon={icon} className="fs-1" />
          </div>
        </div>
        <p
          className={classNames('mb-0 fw-medium text-800 fs--2 module-quick-link-title')}
          style={{ whiteSpace: 'normal', lineHeight: 1.2, minHeight: 30 }}
          title={title}
        >
          {title}
        </p>
        {usageCount > 0 ? (
          <span className="badge badge-soft-secondary rounded-pill mt-1 fs--2">
            {usageCount}
          </span>
        ) : null}
      </Link>
    </Col>
  );
};

QuickLinkItem.propTypes = {
  title: PropTypes.string,
  link: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  usageCount: PropTypes.number,
  onClick: PropTypes.func
};

export default NineDotMenu;
